import React, { useEffect, useState } from "react";
import { kelvinToRGB, getDisplayColor } from "../common/color-util";
import ColorDialog1 from "../twin-widget-dialog/color-dialog-1";
import { SyncBadge } from "../../common/badges";

const Color1Widget = ({ widget, onValueUpdate, pattern }) => {
  const [colorRgb, setColorRgb] = useState("ffffff");
  const [colorTemp, setColorTemp] = useState(2700);
  const [current, setCurrent] = useState("rgb");
  const [widgetData, setWidgetData] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [tempMinMax, setTempMinMax] = useState({ min: 2700, max: 6500 });

  // Determine widget mode based on available items
  const getWidgetMode = () => {
    if (!widgetData) return "all";

    const hasRgb = !!widgetData.items?.$w1;
    const hasTemp = !!widgetData.items?.$w2;
    const hasMode = !!widgetData.items?.$w3;

    if (hasRgb && hasTemp && hasMode) {
      return "all"; // Full color widget with both RGB and temperature
    } else if (hasRgb && !hasTemp) {
      return "rgb"; // RGB-only widget
    } else if (hasTemp && !hasRgb) {
      return "temperature"; // Temperature-only widget
    } else {
      return "all"; // Fallback
    }
  };

  useEffect(() => {
    if (!widget) return;

    setWidgetData(widget);

    if (widget.items?.$w1) {
      const colorValue = widget.items.$w1.value.toLowerCase() || "ffffff";
      setColorRgb(colorValue.replace(/^#/, ""));
    }

    if (widget.items?.$w2) {
      setTempMinMax({
        min: widget.items.$w2.min ?? 2700,
        max: widget.items.$w2.max ?? 6500,
      });
      setColorTemp(widget.items.$w2.value || 2700);
    }

    if (widget.items?.$w3) {
      setCurrent(widget.items.$w3.value || "rgb");
    } else {
      // If no mode selector, determine current based on available data
      if (widget.items?.$w1 && !widget.items?.$w2) {
        setCurrent("rgb");
      } else if (widget.items?.$w2 && !widget.items?.$w1) {
        setCurrent("temperature");
      }
    }
  }, [widget]);

  const handleValueUpdate = (data, type) => {
    if (!widgetData) return;
    const widgetMode = getWidgetMode();

    if (widgetMode === "rgb") {
      // RGB-only mode (like color-rgb-1)
      const updatedWidget = {
        ...widgetData.items.$w1,
        value: data.replace(/^#/, ""),
      };
      onValueUpdate([updatedWidget]);
    } else if (widgetMode === "temperature") {
      // Temperature-only mode (like color-temperature-1)
      const updatedWidget = {
        ...widgetData.items.$w2,
        value: data.toString(),
      };
      onValueUpdate(updatedWidget);
    } else {
      // Full mode with both RGB and temperature
      const updatedWidgets = {};

      if (type === "$w1") {
        updatedWidgets.$w1 = { ...widgetData.items.$w1 };
        updatedWidgets.$w3 = { ...widgetData.items.$w3 };
        updatedWidgets.$w1.value = data.replace(/^#/, "");
        updatedWidgets.$w3.value = "rgb";
        setCurrent("rgb");
      } else if (type === "$w2") {
        updatedWidgets.$w2 = { ...widgetData.items.$w2 };
        updatedWidgets.$w3 = { ...widgetData.items.$w3 };
        updatedWidgets.$w2.value = data;
        updatedWidgets.$w3.value = "temperature";
        setCurrent("temperature");
      }

      onValueUpdate(Object.values(updatedWidgets));
    }
  };

  const handleRgbUpdate = (newColor) => {
    if (!newColor) return;
    setColorRgb(newColor);
    handleValueUpdate(newColor, "$w1");
  };

  const handleTempUpdate = (event, newValue) => {
    event.stopPropagation();

    const adjustedValue = Math.min(
      Math.max(newValue, tempMinMax.min),
      tempMinMax.max
    );

    setColorTemp(adjustedValue);
    handleValueUpdate(adjustedValue, "$w2");
  };

  const getCurrentColor = () => {
    const widgetMode = getWidgetMode();
    const mode =
      widgetMode === "rgb" || current === "rgb" ? "rgb" : "temperature";

    return getDisplayColor(colorRgb || "ffffff", colorTemp, mode);
  };

  const disabled = pattern && pattern.endsWith("_R");
  const widgetMode = getWidgetMode();

  if (!widgetData) {
    return (
      <div className="h-full flex items-center justify-center">
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <div className="relative flex items-center justify-center h-full min-h-fit overflow-hidden">
      {pattern && pattern.endsWith("_W") && <SyncBadge />}
      <div className="w-full h-full">
        <div className="relative h-full aspect-square flex items-center justify-center">
          <div
            onClick={(e) => {
              if (disabled) return;
              e.preventDefault();
              e.stopPropagation();
              setDialogOpen(true);
            }}
            className={`h-[90%] aspect-square rounded-full border transition-transform duration-200 ${
              disabled ? "cursor-default" : "cursor-pointer active:scale-95"
            }`}
            style={{
              backgroundColor: getCurrentColor(),
              border: "1px solid rgba(192, 192, 192, 0.65)",
              opacity: disabled ? 0.8 : 1,
            }}
          />
        </div>
      </div>

      <ColorDialog1
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        colorRgb={colorRgb}
        colorTemp={colorTemp}
        onRgbUpdate={handleRgbUpdate}
        onTempUpdate={handleTempUpdate}
        minTemp={tempMinMax.min}
        maxTemp={tempMinMax.max}
        current={current}
        showControls={widgetMode}
        disabled={disabled}
        brightness={50}
      />
    </div>
  );
};

export default Color1Widget;
